import React, { useContext, useEffect, useState } from 'react';
import { ElementViewerPropsType } from 'components/element-viewer';
import { alpha, Box, Stack, Theme } from '@mui/material';
import ControlsPanel from 'components/element-viewer/ControlsPanel';
import TopControlsWrapper from 'components/element-viewer/common/TopControlsWrapper';
import EmbedElement from 'components/common/external/EmbedElement';
import ExpertCommentaryWall from 'components/wall/ExpertCommentaryWall';
import ViewerWall from 'components/wall/ViewerWall';
import { UserAuth } from 'store/auth';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setElementConciergeVisibility } from 'store/conciergeSlice';
import ReactSelect from 'components/common/react-select/ReactSelect';
import { StaticAddon } from 'components/common/static-addon/StaticAddon';
import useBundleTranslation from 'i18n';
import usePageTitleCrumbs from 'components/element-viewer/hooks/usePageTitleCrumbs';
import { useEmbeddingContextVisible } from 'hooks/useEmbeddingContext';
import HeadingElementData from 'components/element-viewer/HeadingElementData';
import { useIsAdminOrHasPrivilege, useIsUserHasPrivilege } from 'hooks/useUserPrivilege';
import { RootState } from 'store/store';
import { useSelector } from 'react-redux';
import { dispatchCustomEvent } from 'tools/events';
import { DownloadManagerContext } from 'services/DownloadManager';

type FileInCollectionType = {
    label: string;
    value: string;
    url: string;
    download: string;
};
export interface ExternalContentViewerDataType {
    additionalData: any;
    fileCollection: Array<FileInCollectionType>;
    fileId: string;
    urlsData: {
        externalUrl: string;
        url: string;
        urlDownload: string;
    };
}

export default function ExternalContentViewer({
    elementInfo,
    crumbsData,
    segmentValueId,
    onFavoritesChange,
    viewerRequestExtraData,
    alertStatus,
    embeddingType,
}: ElementViewerPropsType<ExternalContentViewerDataType>) {
    const { t } = useBundleTranslation();

    const downloadHandler = useContext(DownloadManagerContext);
    const userAuth: UserAuth = useAppSelector((state) => state.auth);
    const userId = userAuth?.userInfo?.user_id ?? 0;

    const isApp = useSelector((state: RootState) => state.elementViewer.isApp);
    const dimensions = useSelector((state: RootState) => state.elementViewer.dimensions);
    const customEvents = useSelector((state: RootState) => state.elementViewer.customEvents);

    const [fileId, setFileId] = useState(viewerRequestExtraData.fileId);
    const [fileName, setFileName] = useState('');
    const [iframeUrl, setIframeUrl] = useState(viewerRequestExtraData.urlsData.externalUrl);
    const [url, setUrl] = useState(viewerRequestExtraData.urlsData.url);
    const [urlDownload, setUrlDownload] = useState(viewerRequestExtraData.urlsData.urlDownload);

    const handleFileChange = (fileId: string) => {
        const file = viewerRequestExtraData.fileCollection.find((f) => f.value == fileId);
        if (!file) {
            return;
        }
        setFileId(file.value);
        setFileName(file.label);
        setUrl(file.url);
        setIframeUrl(file.url);
        setUrlDownload(file.download);
    };

    const dispatch = useAppDispatch();
    const elementId = elementInfo.row.elementId;
    useEffect(() => {
        dispatch(setElementConciergeVisibility(elementInfo.row.conciergeIconVisibility ?? 'visible'));
    }, [elementId, elementInfo.row.conciergeIconVisibility]);
    useEffect(() => {
        return () => {
            dispatch(setElementConciergeVisibility(null));
        };
    }, []);

    usePageTitleCrumbs(elementInfo, crumbsData);
    const hasWall = useEmbeddingContextVisible('wall') && elementInfo.row.showCollaborationInd;
    const hasExtLink = useEmbeddingContextVisible('extLink');

    const iframeHeight =
        isApp && dimensions && dimensions.height
            ? dimensions.height - 120
            : elementInfo.row.calcIframeHeightOnImageAspectRatioInd
              ? 0
              : elementInfo.row.externalReportIframeHeight;

    const embedElement = (
        <EmbedElement
            url={url.replace('/download/Y', '')}
            embedUrl={iframeUrl}
            displayType={elementInfo.row.externalContentDisplayInViewer}
            iframeHeight={iframeHeight}
        />
    );

    const canAddExpAnnotationOwner = useIsUserHasPrivilege('PRIV_CAN_ADD_EXPERT_COMMENTARY_ONLY_IF_OWNER');
    const isOwner =
        userId > 0 &&
        ['businessOwnerId', 'dataStewardId', 'technicalOwnerId'].some(
            (type) => (elementInfo as any).row[type] == userId,
        );
    const canAddExpAnnotation = useIsAdminOrHasPrivilege('PRIV_CAN_ADD_EXPERT_COMMENTARY');
    const expertCommentaryAllowEdit = (isOwner && canAddExpAnnotationOwner) || canAddExpAnnotation;

    if (embeddingType == 'short') {
        return embedElement;
    }

    const hasDownloadLink = viewerRequestExtraData.urlsData.urlDownload.length > 0;
    const showExtLinkBlock = hasExtLink && (hasDownloadLink || viewerRequestExtraData.urlsData.url.length > 0);

    const clickExternalLink = (event: any) => {
        if (isApp && customEvents && customEvents.external_link_click) {
            event.preventDefault();

            const payloadUrl = hasDownloadLink ? urlDownload : url.replace('/download/Y', '');

            dispatchCustomEvent('mi-element-viewer', {
                action: 'external-link-click',
                payload: {
                    url: payloadUrl,
                    elementInfo,
                },
            });
        }
    };

    const handleStartDownload = () => {
        downloadHandler(
            { elementId: elementInfo.row.elementId, segmentId: segmentValueId, format: '' },
            {
                fileId: fileId,
            },
        );
    };

    return (
        <>
            <TopControlsWrapper>
                <HeadingElementData element={elementInfo} />
                <ControlsPanel
                    element={elementInfo}
                    segmentValueId={segmentValueId}
                    onFavoritesChange={onFavoritesChange}
                    alertStatus={alertStatus}
                />
            </TopControlsWrapper>
            <Box
                sx={{
                    pt: 2,
                    pb: 5,
                    overflow: 'auto',
                    px: (theme) => theme.size.pxValue(theme.size.containerIndentX),
                    flexGrow: 1,
                }}
                className={'scroll-content-container'}
            >
                {viewerRequestExtraData.fileCollection.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                        <Stack direction="row">
                            <StaticAddon>{t('file')}</StaticAddon>
                            <Box sx={{ width: '175px' }}>
                                <ReactSelect
                                    data={viewerRequestExtraData.fileCollection}
                                    value={
                                        fileId == '0' || fileId == ''
                                            ? viewerRequestExtraData.fileCollection[0].value
                                            : fileId
                                    }
                                    name={'file_id'}
                                    update={handleFileChange}
                                />
                            </Box>
                        </Stack>
                    </Box>
                )}
                {embedElement}

                {showExtLinkBlock && (
                    <Box
                        sx={{
                            textAlign: 'right',
                            position: 'relative',
                        }}
                    >
                        <Box sx={{ position: 'absolute', right: 0, top: 0, mt: 2 }}>
                            <Box
                                onClick={(event: any) =>
                                    hasDownloadLink && !isApp ? handleStartDownload() : clickExternalLink(event)
                                }
                                data-test={'external-content-external-link'}
                                component={hasDownloadLink && !isApp ? 'span' : 'a'}
                                href={hasDownloadLink ? urlDownload : url.replace('/download/Y', '')}
                                target={'_blank'}
                                rel="noopener noreferrer"
                                className={'default-link'}
                                sx={{
                                    border: '1px solid',
                                    borderColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.08),
                                    borderRadius: '4px',
                                    padding: '7px 12px',
                                    '&:hover': {
                                        textDecoration: 'none',
                                        backgroundColor: (theme: Theme) => alpha(theme.palette.text.primary, 0.04),
                                    },
                                }}
                            >
                                {viewerRequestExtraData.urlsData.urlDownload.length > 0 ? (
                                    <>
                                        {t('download')} {elementInfo.row.externalReportReferenceName}
                                    </>
                                ) : (
                                    <>
                                        {t('open')} {elementInfo.row.name}
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                )}

                {hasWall && (
                    <Box sx={{ pt: 2, mx: 'auto', width: '552px' }}>
                        <ExpertCommentaryWall
                            data={{}}
                            elementId={elementInfo.row.elementId}
                            segmentValueId={segmentValueId}
                            userId={userId}
                            elementType={'other external content'}
                            allowEdit={expertCommentaryAllowEdit}
                        />
                        <ViewerWall
                            wallType="chart"
                            data={{}}
                            elementId={elementInfo.row.elementId}
                            segmentValueId={segmentValueId}
                            elementType={'other external content'}
                            userId={userId}
                        />
                    </Box>
                )}
            </Box>
        </>
    );
}
